// The contract for what 3CX call journaling posts to POST /ops/call-journal.
//
// The wire keys are 3CX's (PascalCase, chosen in the CRM template); the parsed
// shape is ours (camelCase). One place knows both.
//
// EMPTY STRING BECOMES NULL. 3CX sends "" for a value it has nothing for —
// notably on Missed/Notanswered calls, where the vendor template omits the
// transcription block entirely. An empty string would render in the console as
// "there is a transcript and it is blank", which is a lie about a call nobody
// answered.
import { z } from 'zod';
/**
 * A bracketed single word — `[Transcription]`, `[Summary]` — is 3CX echoing
 * an unresolved template placeholder back literally, verbatim, rather than
 * filling it in. That is not a value; it is the template failing to
 * substitute, and treating it as one would render a fabricated "transcript"
 * that is just the field's own name in brackets.
 */
const UNRESOLVED_PLACEHOLDER = /^\[[A-Za-z]+\]$/;
/** "" and whitespace-only become null; a real value is trimmed of nothing. */
const emptyToNull = z
    .string()
    .optional()
    .transform((v) => (v === undefined || v.trim() === '' || UNRESOLVED_PLACEHOLDER.test(v.trim()) ? null : v));
export const callJournalSchema = z
    .object({
    CallStartTimeUTC: z.string().min(1),
    CallEndTimeUTC: z.string().min(1),
    Number: z.string().min(1),
    Agent: z.string().min(1),
    CallType: z.enum(['Inbound', 'Outbound', 'Missed', 'Notanswered']),
    Transcription: emptyToNull,
    Summary: emptyToNull,
    Sentiment: emptyToNull,
    RecordingUrl: emptyToNull,
})
    .transform((raw) => ({
    callStartUtc: raw.CallStartTimeUTC,
    callEndUtc: raw.CallEndTimeUTC,
    number: raw.Number,
    agent: raw.Agent,
    callType: raw.CallType,
    callTranscript: raw.Transcription,
    callSummary: raw.Summary,
    callSentiment: raw.Sentiment,
    recordingUrl: raw.RecordingUrl,
}))
    .pipe(z.object({
    callStartUtc: z.string(),
    callEndUtc: z.string(),
    number: z.string(),
    agent: z.string(),
    callType: z.enum(['Inbound', 'Outbound', 'Missed', 'Notanswered']),
    callTranscript: z.string().nullable(),
    callSummary: z.string().nullable(),
    callSentiment: z.string().nullable(),
    recordingUrl: z.string().nullable(),
}));
